import { apiClient } from "@/shared/apiClient";
import { messageSchema, paginatedSchema, sendMessageRequestSchema } from "@/shared/zod";
import type { Message, Paginated } from "./types";
import type { SendMessageRequest } from "@/features/tasks/types";

function transformExternalMessage(message: any): Message {
  // Try multiple field names for photo URL (backend might use different names)
  const photoUrl = message.media_url || message.photo_url || message.photoUrl || message.mediaUrl || null;
  
  // Debug logging to see what we're receiving
  if (photoUrl) {
  }
  
  return {
    id: message.id,
    taskId: message.task_id,
    createdAt: message.created_at,
    direction: message.direction,
    senderName: message.direction === "incoming" ? "You" : "System",
    body: message.text || message.content || message.body || "",
    photoUrl,
  };
}

export const messagesApi = {
  async getMessages(cursor?: string): Promise<Paginated<Message>> {
    try {
      const searchParams = new URLSearchParams();
      if (cursor) searchParams.set("cursor", cursor);
      
      const queryString = searchParams.toString();
      const endpoint = `/api/employee/messages${queryString ? `?${queryString}` : ""}`;
      
      const response = await apiClient.get(endpoint);
      
      // Transform external API response to match our expected format
      const messages = response.data || [];
      const transformedMessages = messages.map(transformExternalMessage);
      
      const transformedResponse = {
        data: transformedMessages.reverse(), // Reverse the messages array to show newest at bottom
        nextCursor: response.nextCursor || null,
      };
      
      return apiClient.validateResponse(transformedResponse, paginatedSchema(messageSchema));
    } catch (error) {
      console.error("Error in getMessages:", error);
      throw error;
    }
  },

  async sendMessage(data: SendMessageRequest): Promise<Message> {
    try {
      // Validate request data
      sendMessageRequestSchema.parse(data);
      
      const payload: any = {
        content: data.body || "", // Can be empty if photoUrl is provided
        message_type: data.photoUrl ? "photo" : "text",
        direction: "incoming",
        status: "pending",
      };

      // Add photoUrl if photo was uploaded
      if (data.photoUrl) {
        payload.photoUrl = data.photoUrl;
      }
      
      const response = await apiClient.post(`/api/employee/messages`, payload);
      
      // Transform the response to match our Message interface
      const messageData = {
        id: response.id || Date.now(),
        taskId: response.taskId || null,
        createdAt: response.createdAt || new Date().toISOString(),
        direction: response.direction || "incoming",
        senderName: response.senderName || "You",
        body: response.body || response.content || data.body || "",
        photoUrl: response.photo_url || response.photoUrl || data.photoUrl || null,
      };
      
      return apiClient.validateResponse(messageData, messageSchema);
    } catch (error) {
      console.error("Error in sendMessage:", error);
      throw error;
    }
  },
};
